import { useEffect, useRef, useCallback, useState } from 'react';
import * as PIXI from 'pixi.js';
import { RoomInfo, CarState, CAR_COLORS, Track, CarColor } from '@shared';
import { useGameStore } from '../store/gameStore';
import { useNetworkStore } from '../store/networkStore';
import { debugLogger } from '../utils/debugLogger';

// Module-level tracker to survive React strict mode cleanup/remount cycles
let moduleApp: PIXI.Application | null = null;
let moduleTrackContainer: PIXI.Container | null = null;
let cleanupTimeoutId: number | null = null;
let moduleInitializing = false; // Sync flag set BEFORE async work

interface GameRendererProps {
  containerRef: React.RefObject<HTMLDivElement>;
  room: RoomInfo;
  localPlayerId: string | null;
}

function GameRenderer({ containerRef, room, localPlayerId }: GameRendererProps) {
  const appRef = useRef<PIXI.Application | null>(null);
  const isInitializingRef = useRef(false);
  const isDestroyedRef = useRef(false); // Guard against render after cleanup
  const carsRef = useRef<Map<string, PIXI.Container>>(new Map());
  const trackContainerRef = useRef<PIXI.Container | null>(null);
  const tireMarksContainerRef = useRef<PIXI.Container | null>(null);
  const cameraRef = useRef({ x: 0, y: 0 });
  const lastTimeRef = useRef<number>(performance.now());
  const debugCounterRef = useRef(0);
  const [pixiReady, setPixiReady] = useState(false);
  
  // Track previous positions for tire marks
  const prevCarPositionsRef = useRef<Map<string, { x: number; y: number; rotation: number }>>(new Map());
  const tireMarkCountRef = useRef(0);
  const MAX_TIRE_MARKS = 500; // Limit total marks for performance
  
  const { interpolateCars } = useGameStore();
  const currentTrack = useNetworkStore(state => state.track);

  // Initialize PIXI
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Cancel any pending cleanup from strict mode
    if (cleanupTimeoutId !== null) {
      clearTimeout(cleanupTimeoutId);
      cleanupTimeoutId = null;
      debugLogger.log('PIXI', 'Cancelled pending cleanup (strict mode remount)');
    }
    
    // If we already have a module-level app, reuse it
    if (moduleApp && moduleTrackContainer) {
      debugLogger.log('PIXI', 'Reusing existing PIXI app');
      appRef.current = moduleApp;
      trackContainerRef.current = moduleTrackContainer;
      isDestroyedRef.current = false;
      
      // Re-add canvas if needed
      if (!containerRef.current.contains(moduleApp.canvas)) {
        containerRef.current.appendChild(moduleApp.canvas);
      }
      
      // Re-add render loop
      moduleApp.ticker.add(renderLoop);
      setPixiReady(true);
      
      if (!moduleApp.ticker.started) {
        moduleApp.ticker.start();
      }
      return;
    }
    
    // Check module-level initializing flag (survives React strict mode)
    // If another instance is initializing, wait for it
    if (moduleInitializing && !moduleApp) {
      debugLogger.log('PIXI', 'Module initializing elsewhere, waiting...');
      const waitForApp = setInterval(() => {
        if (moduleApp && moduleTrackContainer) {
          clearInterval(waitForApp);
          debugLogger.log('PIXI', 'Module app ready, using it');
          appRef.current = moduleApp;
          trackContainerRef.current = moduleTrackContainer;
          isDestroyedRef.current = false;
          
          if (!containerRef.current!.contains(moduleApp.canvas)) {
            containerRef.current!.appendChild(moduleApp.canvas);
          }
          
          moduleApp.ticker.add(renderLoop);
          setPixiReady(true);
          
          if (!moduleApp.ticker.started) {
            moduleApp.ticker.start();
          }
        }
      }, 50);
      return () => clearInterval(waitForApp);
    }
    
    if (isInitializingRef.current) return;
    
    // Set BOTH flags before any async work
    moduleInitializing = true;
    isInitializingRef.current = true;
    isDestroyedRef.current = false;

    const app = new PIXI.Application();
    
    const initApp = async () => {
      await app.init({
        width: containerRef.current!.clientWidth,
        height: containerRef.current!.clientHeight,
        backgroundColor: 0x1a1a2e, // Dark blue background
        antialias: true,
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
        autoStart: false, // Don't start ticker until scene is ready
      });

      debugLogger.log('PIXI', 'Canvas created', {
        canvasWidth: app.canvas.width,
        canvasHeight: app.canvas.height,
      });

      containerRef.current!.appendChild(app.canvas);
      appRef.current = app;
      moduleApp = app; // Store at module level

      // Create track container
      const trackContainer = new PIXI.Container();
      app.stage.addChild(trackContainer);
      trackContainerRef.current = trackContainer;
      moduleTrackContainer = trackContainer; // Store at module level

      // Add render loop but don't start yet
      app.ticker.add(renderLoop);
      
      // Mark PIXI as ready - this will trigger track drawing
      setPixiReady(true);
      
      // Start the ticker AFTER pixiReady is set (track will be drawn in next effect)
      // Use setTimeout to ensure React state update completes first
      setTimeout(() => {
        if (appRef.current && !isDestroyedRef.current) {
          appRef.current.ticker.start();
        }
      }, 100);
    };

    initApp();

    // Handle resize
    const handleResize = () => {
      if (!appRef.current || !containerRef.current) return;
      appRef.current.renderer.resize(
        containerRef.current.clientWidth,
        containerRef.current.clientHeight
      );
    };

    window.addEventListener('resize', handleResize);

    return () => {
      // Set destroyed flag FIRST to stop render loop immediately
      isDestroyedRef.current = true;
      window.removeEventListener('resize', handleResize);
      
      if (appRef.current) {
        // Remove the render callback
        appRef.current.ticker.remove(renderLoop);
        
        // Delay actual destruction to allow React strict mode remount
        cleanupTimeoutId = window.setTimeout(() => {
          cleanupTimeoutId = null;
          debugLogger.log('PIXI', 'Executing delayed cleanup');
          if (moduleApp) {
            moduleApp.ticker.stop();
            moduleApp.destroy(true, { children: true });
            moduleApp = null;
            moduleTrackContainer = null;
          }
          moduleInitializing = false; // Only reset after actual cleanup
        }, 100) as unknown as number;
        
        appRef.current = null;
      }
      trackContainerRef.current = null;
      carsRef.current.clear();
      isInitializingRef.current = false;
      setPixiReady(false);
    };
  }, [containerRef]);

  // Render loop
  const renderLoop = useCallback(() => {
    // Exit early if destroyed or not properly initialized
    if (isDestroyedRef.current || !appRef.current || !trackContainerRef.current) return;

    // Calculate delta time
    const currentTime = performance.now();
    let deltaTime = (currentTime - lastTimeRef.current) / 1000;
    lastTimeRef.current = currentTime;

    // Clamp deltaTime to prevent issues with large time gaps (e.g., tab switching)
    if (deltaTime > 0.1) {
      deltaTime = 0.016; // Default to ~60fps
    }

    // Interpolate car positions for smooth display
    interpolateCars(deltaTime);

    const { cars: currentCars } = useGameStore.getState();
    const screenWidth = appRef.current.renderer.width;
    const screenHeight = appRef.current.renderer.height;

    // Update camera to follow local player
    if (localPlayerId && currentCars.has(localPlayerId)) {
      const localCar = currentCars.get(localPlayerId)!;
      const targetX = localCar.displayPosition.x;
      const targetY = localCar.displayPosition.y;
      
      // Validate target position
      if (!Number.isFinite(targetX) || !Number.isFinite(targetY)) {
        console.error('Invalid car displayPosition for camera:', { targetX, targetY, playerId: localPlayerId });
      } else {
        // On first frame, snap camera to car position
        if (cameraRef.current.x === 0 && cameraRef.current.y === 0) {
          cameraRef.current.x = targetX;
          cameraRef.current.y = targetY;
        } else {
          // Smooth camera follow
          const lerpFactor = 0.1;
          cameraRef.current.x += (targetX - cameraRef.current.x) * lerpFactor;
          cameraRef.current.y += (targetY - cameraRef.current.y) * lerpFactor;
        }
      }
    }

    // Validate camera position (clamp to reasonable bounds)
    // If out of bounds, try to reset to spawn point or track center
    const getResetPosition = () => {
      if (currentTrack?.spawnPoints?.[0]) {
        return { x: currentTrack.spawnPoints[0].position.x, y: currentTrack.spawnPoints[0].position.y };
      }
      if (currentTrack) {
        return { x: currentTrack.width / 2, y: currentTrack.height / 2 };
      }
      return { x: 0, y: 0 };
    };
    
    if (!Number.isFinite(cameraRef.current.x) || Math.abs(cameraRef.current.x) > 100000) {
      console.error('Camera X out of bounds, resetting:', cameraRef.current.x);
      const reset = getResetPosition();
      cameraRef.current.x = reset.x;
      cameraRef.current.y = reset.y;
    }
    if (!Number.isFinite(cameraRef.current.y) || Math.abs(cameraRef.current.y) > 100000) {
      console.error('Camera Y out of bounds, resetting:', cameraRef.current.y);
      const reset = getResetPosition();
      cameraRef.current.x = reset.x;
      cameraRef.current.y = reset.y;
    }

    // Apply camera transform
    trackContainerRef.current.x = screenWidth / 2 - cameraRef.current.x;
    trackContainerRef.current.y = screenHeight / 2 - cameraRef.current.y;

    // Debug log every 60 frames (~1 second)
    debugCounterRef.current++;
    if (debugCounterRef.current % 60 === 0) {
      debugLogger.log('RENDER', 'Render state', {
        carsInStore: currentCars.size,
        carsSprites: carsRef.current.size,
        trackChildren: trackContainerRef.current.children.length,
        camera: { x: cameraRef.current.x.toFixed(0), y: cameraRef.current.y.toFixed(0) },
        containerPos: { x: trackContainerRef.current.x.toFixed(0), y: trackContainerRef.current.y.toFixed(0) },
        screen: { w: screenWidth, h: screenHeight },
        localPlayerId,
        hasLocalCar: localPlayerId ? currentCars.has(localPlayerId) : false,
      });
    }

    // Update car sprites
    currentCars.forEach((carData, id) => {
      let carContainer = carsRef.current.get(id);
      
      if (!carContainer) {
        carContainer = createCarSprite(carData);
        trackContainerRef.current!.addChild(carContainer);
        carsRef.current.set(id, carContainer);
      }

      // Update position and rotation
      carContainer.x = carData.displayPosition.x;
      carContainer.y = carData.displayPosition.y;
      carContainer.rotation = carData.displayRotation;

      // Update front wheel rotation based on steering
      // For local player, use current input for immediate response
      // For remote players, use the server-synced steering angle
      const { currentInput } = useGameStore.getState();
      const isLocalPlayer = id === localPlayerId;
      const steerAngle = isLocalPlayer ? (currentInput.steerValue || 0) : (carData.steeringAngle || 0);
      
      carContainer.children.forEach(child => {
        if (child.label === 'frontWheel0' || child.label === 'frontWheel1') {
          (child as PIXI.Container).rotation = steerAngle * 0.4; // Max ~23 degree visual turn
        }
      });

      // Update visibility
      carContainer.visible = true;
      
      // === TIRE MARKS ===
      if (tireMarksContainerRef.current) {
        const prevPos = prevCarPositionsRef.current.get(id);
        const currentPos = {
          x: carData.displayPosition.x,
          y: carData.displayPosition.y,
          rotation: carData.displayRotation,
        };
        
        if (prevPos) {
          // Calculate car's forward direction
          const forwardX = Math.sin(currentPos.rotation);
          const forwardY = -Math.cos(currentPos.rotation);
          
          // Calculate velocity direction from position change
          const dx = currentPos.x - prevPos.x;
          const dy = currentPos.y - prevPos.y;
          const speed = Math.sqrt(dx * dx + dy * dy);
          
          // Only draw marks if car is moving
          if (speed > 0.5) {
            // Calculate how much the car is sliding (lateral velocity)
            // Dot product of velocity with perpendicular to forward direction
            const perpX = -forwardY;
            const perpY = forwardX;
            const lateralVelocity = Math.abs(dx * perpX + dy * perpY);
            
            // Also check angular velocity (spinning)
            const rotationChange = Math.abs(currentPos.rotation - prevPos.rotation);
            
            // Check if handbraking (from local player input)
            const isHandbraking = isLocalPlayer && currentInput.handbrake;
            
            // Calculate skid intensity (0-1)
            // Higher when: lateral sliding, spinning, handbraking, or during sharp turns at speed
            let skidIntensity = 0;
            
            // Lateral sliding contribution
            if (speed > 0) {
              skidIntensity += (lateralVelocity / speed) * 0.8;
            }
            
            // Rotation contribution (spinning)
            skidIntensity += Math.min(rotationChange * 3, 0.4);
            
            // Handbrake bonus
            if (isHandbraking && speed > 2) {
              skidIntensity += 0.5;
            }
            
            // Sharp turn at speed
            const turnIntensity = Math.abs(steerAngle) * speed * 0.02;
            skidIntensity += Math.min(turnIntensity, 0.3);
            
            // Clamp to 0-1
            skidIntensity = Math.min(1, Math.max(0, skidIntensity));
            
            // Draw tire marks if skid intensity is significant
            if (skidIntensity > 0.15 && tireMarkCountRef.current < MAX_TIRE_MARKS) {
              // Wheel positions relative to car center
              const wheelOffsets = [
                { x: -15, y: 18 },  // Rear left
                { x: 15, y: 18 },   // Rear right
              ];
              
              // Transform wheel positions to world coordinates
              const cos = Math.cos(currentPos.rotation);
              const sin = Math.sin(currentPos.rotation);
              
              wheelOffsets.forEach(offset => {
                const worldX = currentPos.x + offset.x * cos - offset.y * sin;
                const worldY = currentPos.y + offset.x * sin + offset.y * cos;
                
                const prevWorldX = prevPos.x + offset.x * Math.cos(prevPos.rotation) - offset.y * Math.sin(prevPos.rotation);
                const prevWorldY = prevPos.y + offset.x * Math.sin(prevPos.rotation) + offset.y * Math.cos(prevPos.rotation);
                
                // Draw tire mark
                const mark = new PIXI.Graphics();
                mark.moveTo(prevWorldX, prevWorldY);
                mark.lineTo(worldX, worldY);
                
                // Darker and thicker marks for harder skids
                const alpha = 0.2 + skidIntensity * 0.5;
                const width = 2 + skidIntensity * 4;
                mark.stroke({ color: 0x1a1a1a, width, alpha });
                
                tireMarksContainerRef.current!.addChild(mark);
                tireMarkCountRef.current++;
              });
              
              // Clean up old marks if we exceed the limit
              if (tireMarkCountRef.current > MAX_TIRE_MARKS) {
                const toRemove = tireMarkCountRef.current - MAX_TIRE_MARKS + 50; // Remove in batches
                for (let i = 0; i < toRemove && tireMarksContainerRef.current!.children.length > 0; i++) {
                  const oldMark = tireMarksContainerRef.current!.children[0];
                  tireMarksContainerRef.current!.removeChild(oldMark);
                  oldMark.destroy();
                  tireMarkCountRef.current--;
                }
              }
            }
          }
        }
        
        // Update previous position
        prevCarPositionsRef.current.set(id, currentPos);
      }
    });

    // Remove cars that are no longer in the game
    carsRef.current.forEach((container, id) => {
      if (!currentCars.has(id)) {
        trackContainerRef.current!.removeChild(container);
        container.destroy();
        carsRef.current.delete(id);
      }
    });
  }, [localPlayerId, interpolateCars]);

  // Create car sprite
  const createCarSprite = (carData: CarState): PIXI.Container => {
    const container = new PIXI.Container();
    
    const player = room.players.find((p) => p.id === carData.playerId);
    const colorData = player ? CAR_COLORS[player.color as CarColor] : CAR_COLORS.red;
    const color = parseInt(colorData?.hex?.replace('#', '') || 'ff0000', 16);

    // Car body
    const body = new PIXI.Graphics();
    body.roundRect(-15, -25, 30, 50, 5);
    body.fill(color);
    container.addChild(body);

    // Windshield
    const windshield = new PIXI.Graphics();
    windshield.roundRect(-10, -15, 20, 15, 3);
    windshield.fill(0x1a1a2e);
    container.addChild(windshield);

    // Rear wheels (static)
    const rearWheelPositions = [
      { x: -15, y: 18 },
      { x: 15, y: 18 },
    ];

    rearWheelPositions.forEach(pos => {
      const wheel = new PIXI.Graphics();
      wheel.roundRect(-3, -5, 6, 10, 2);
      wheel.fill(0x1a1a1a);
      wheel.x = pos.x;
      wheel.y = pos.y;
      container.addChild(wheel);
    });

    // Front wheels (will rotate with steering)
    const frontWheelPositions = [
      { x: -15, y: -18 },
      { x: 15, y: -18 },
    ];

    frontWheelPositions.forEach((pos, index) => {
      const wheelContainer = new PIXI.Container();
      wheelContainer.x = pos.x;
      wheelContainer.y = pos.y;
      
      const wheel = new PIXI.Graphics();
      wheel.roundRect(-3, -5, 6, 10, 2);
      wheel.fill(0x1a1a1a);
      wheelContainer.addChild(wheel);
      
      // Tag for identification
      wheelContainer.label = `frontWheel${index}`;
      container.addChild(wheelContainer);
    });

    // Headlights
    const headlight1 = new PIXI.Graphics();
    headlight1.circle(-8, -23, 3);
    headlight1.fill(0xffffcc);
    container.addChild(headlight1);

    const headlight2 = new PIXI.Graphics();
    headlight2.circle(8, -23, 3);
    headlight2.fill(0xffffcc);
    container.addChild(headlight2);

    // Taillights
    const taillight1 = new PIXI.Graphics();
    taillight1.roundRect(-12, 20, 6, 4, 1);
    taillight1.fill(0xff0000);
    container.addChild(taillight1);

    const taillight2 = new PIXI.Graphics();
    taillight2.roundRect(6, 20, 6, 4, 1);
    taillight2.fill(0xff0000);
    container.addChild(taillight2);

    // Player name
    const nameText = new PIXI.Text({
      text: player?.nickname || 'Player',
      style: {
        fontSize: 12,
        fill: 0xffffff,
        fontFamily: 'Inter, sans-serif',
        stroke: { color: 0x000000, width: 2 },
      },
    });
    nameText.anchor.set(0.5, 0.5);
    nameText.y = -40;
    container.addChild(nameText);

    return container;
  };

  // Draw track when it changes or when PIXI becomes ready
  useEffect(() => {
    debugLogger.log('TRACK', 'Track effect triggered', { 
      pixiReady,
      hasContainer: !!trackContainerRef.current, 
      hasTrack: !!currentTrack,
      trackId: currentTrack?.id,
      trackElements: currentTrack?.elements?.length 
    });
    if (!pixiReady || !trackContainerRef.current || !currentTrack) return;

    // Clear existing track elements (except cars)
    const carContainers = Array.from(carsRef.current.values());
    trackContainerRef.current.removeChildren();
    
    // Draw track elements
    debugLogger.log('TRACK', 'Drawing track', { name: currentTrack.name, width: currentTrack.width, height: currentTrack.height });
    drawTrack(currentTrack, trackContainerRef.current);
    
    // Create tire marks container (above track, below cars)
    const tireMarksContainer = new PIXI.Container();
    tireMarksContainer.label = 'tireMarks';
    trackContainerRef.current.addChild(tireMarksContainer);
    tireMarksContainerRef.current = tireMarksContainer;
    tireMarkCountRef.current = 0;
    prevCarPositionsRef.current.clear();
    
    // Re-add car containers on top
    carContainers.forEach(car => trackContainerRef.current!.addChild(car));
    
    debugLogger.log('TRACK', 'Track drawn', { 
      childCount: trackContainerRef.current.children.length,
      containerVisible: trackContainerRef.current.visible,
      containerAlpha: trackContainerRef.current.alpha,
      containerBounds: {
        x: trackContainerRef.current.x,
        y: trackContainerRef.current.y,
      },
      firstChildType: trackContainerRef.current.children[0]?.constructor?.name,
    });
  }, [currentTrack, pixiReady]);

  const drawTrack = (track: Track, container: PIXI.Container) => {
    // Draw track background
    const background = new PIXI.Graphics();
    background.rect(0, 0, track.width, track.height);
    background.fill(0x1a1a2e); // Dark blue track background
    container.addChildAt(background, 0);
    debugLogger.log('TRACK', 'Background added', { width: track.width, height: track.height });

    // Draw road paths
    if (track.roadPath && track.roadPath.length > 0) {
      track.roadPath.forEach(path => {
        const points = path.points;
        if (points && points.length > 1) {
          const roadGraphics = new PIXI.Graphics();
          const firstPoint = points[0]!;
          roadGraphics.moveTo(firstPoint.x, firstPoint.y);
          for (let i = 1; i < points.length; i++) {
            const point = points[i]!;
            roadGraphics.lineTo(point.x, point.y);
          }
          roadGraphics.stroke({ color: 0x3a3a5e, width: path.width || 200 });
          container.addChildAt(roadGraphics, 1);
        }
      });
    }

    // Draw boundaries (walls)
    if (track.boundaries && track.boundaries.length > 0) {
      track.boundaries.forEach(boundary => {
        const points = boundary.points;
        if (points && points.length > 1) {
          const wallGraphics = new PIXI.Graphics();
          const firstPoint = points[0]!;
          wallGraphics.moveTo(firstPoint.x, firstPoint.y);
          for (let i = 1; i < points.length; i++) {
            const point = points[i]!;
            wallGraphics.lineTo(point.x, point.y);
          }
          if (boundary.isClosed) {
            wallGraphics.closePath();
          }
          wallGraphics.stroke({ color: 0xff4444, width: 8 });
          container.addChild(wallGraphics);
        }
      });
    }

    // Draw checkpoints
    if (track.checkpoints && track.checkpoints.length > 0) {
      track.checkpoints.forEach(checkpoint => {
        const cpGraphics = new PIXI.Graphics();
        const halfWidth = (checkpoint.width || 200) / 2;
        
        // Draw checkpoint line
        cpGraphics.moveTo(-halfWidth, 0);
        cpGraphics.lineTo(halfWidth, 0);
        cpGraphics.stroke({ color: 0xffff00, width: 4, alpha: 0.5 });
        
        cpGraphics.x = checkpoint.position.x;
        cpGraphics.y = checkpoint.position.y;
        cpGraphics.rotation = checkpoint.rotation || 0;
        container.addChild(cpGraphics);
      });
    }

    // Draw start/finish line - checkered flag pattern
    if (track.startFinishLine) {
      const finishContainer = new PIXI.Container();
      const halfWidth = (track.startFinishLine.width || 200) / 2;
      const squareSize = 10;
      
      // In PIXI 8, we need to draw and fill each rect separately
      for (let i = -halfWidth; i < halfWidth; i += squareSize) {
        for (let j = -10; j < 10; j += squareSize) {
          const isWhite = (Math.floor((i + halfWidth) / squareSize) + Math.floor((j + 10) / squareSize)) % 2 === 0;
          const square = new PIXI.Graphics();
          square.rect(i, j, squareSize, squareSize);
          square.fill(isWhite ? 0xffffff : 0x000000);
          finishContainer.addChild(square);
        }
      }
      
      finishContainer.x = track.startFinishLine.position.x;
      finishContainer.y = track.startFinishLine.position.y;
      finishContainer.rotation = track.startFinishLine.rotation || 0;
      container.addChild(finishContainer);
    }

    // Draw elements (roads, walls, boost pads, etc. from track editor)
    if (track.elements && track.elements.length > 0) {
      // Sort elements so roads are drawn first, then other elements, then walls on top
      const sortedElements = [...track.elements].sort((a, b) => {
        const order: Record<string, number> = {
          road: 0,
          spawn: 1,
          checkpoint: 2,
          boost_pad: 3,
          boost: 3,
          oil_slick: 4,
          oil: 4,
          ramp: 5,
          finish: 6,
          wall: 7,
        };
        return (order[a.type] || 0) - (order[b.type] || 0);
      });

      sortedElements.forEach((element) => {
        const x = element.x ?? element.position?.x ?? 0;
        const y = element.y ?? element.position?.y ?? 0;
        const width = element.width || 100;
        const height = element.height || 100;
        
        switch (element.type) {
          case 'road': {
            const graphics = new PIXI.Graphics();
            graphics.rect(x, y, width, height);
            graphics.fill(0x3a3a5e);
            container.addChild(graphics);
            break;
          }
          case 'wall': {
            const graphics = new PIXI.Graphics();
            graphics.rect(x, y, width, height);
            graphics.fill(0x8B0000);
            const border = new PIXI.Graphics();
            border.rect(x, y, width, height);
            border.stroke({ color: 0xff4444, width: 2 });
            container.addChild(graphics);
            container.addChild(border);
            break;
          }
          case 'finish': {
            // In PIXI 8, each rect needs its own Graphics with immediate fill
            const finishContainer = new PIXI.Container();
            const squareSize = 10;
            
            for (let i = 0; i < width; i += squareSize) {
              for (let j = 0; j < height; j += squareSize) {
                const isWhite = (Math.floor(i / squareSize) + Math.floor(j / squareSize)) % 2 === 0;
                const square = new PIXI.Graphics();
                square.rect(x + i, y + j, squareSize, squareSize);
                square.fill(isWhite ? 0xffffff : 0x000000);
                finishContainer.addChild(square);
              }
            }
            
            if (element.rotation) {
              finishContainer.pivot.set(x + width / 2, y + height / 2);
              finishContainer.position.set(x + width / 2, y + height / 2);
              finishContainer.rotation = element.rotation;
            }
            container.addChild(finishContainer);
            break;
          }
          case 'checkpoint': {
            const graphics = new PIXI.Graphics();
            graphics.rect(x, y, width, height);
            graphics.fill({ color: 0xffff00, alpha: 0.3 });
            const border = new PIXI.Graphics();
            border.rect(x, y, width, height);
            border.stroke({ color: 0xffff00, width: 2 });
            container.addChild(graphics);
            container.addChild(border);
            break;
          }
          case 'spawn': {
            const graphics = new PIXI.Graphics();
            graphics.rect(x, y, width, height);
            graphics.fill({ color: 0x00ff00, alpha: 0.3 });
            const border = new PIXI.Graphics();
            border.rect(x, y, width, height);
            border.stroke({ color: 0x00ff00, width: 2 });
            // Draw arrow to show spawn direction
            const arrow = new PIXI.Graphics();
            const cx = x + width / 2;
            const cy = y + height / 2;
            arrow.moveTo(cx, cy - 10);
            arrow.lineTo(cx + 8, cy + 5);
            arrow.lineTo(cx - 8, cy + 5);
            arrow.closePath();
            arrow.fill(0x00ff00);
            container.addChild(graphics);
            container.addChild(border);
            container.addChild(arrow);
            break;
          }
          case 'boost_pad':
          case 'boost': {
            const graphics = new PIXI.Graphics();
            graphics.rect(x, y, width, height);
            graphics.fill({ color: 0x00ffff, alpha: 0.4 });
            // Draw arrows to indicate boost direction
            const arrows = new PIXI.Graphics();
            const bcx = x + width / 2;
            const bcy = y + height / 2;
            arrows.moveTo(bcx - 15, bcy + 10);
            arrows.lineTo(bcx, bcy - 15);
            arrows.lineTo(bcx + 15, bcy + 10);
            arrows.stroke({ color: 0x00ffff, width: 3 });
            container.addChild(graphics);
            container.addChild(arrows);
            break;
          }
          case 'oil_slick':
          case 'oil': {
            const graphics = new PIXI.Graphics();
            graphics.ellipse(x + width / 2, y + height / 2, width / 2, height / 2);
            graphics.fill({ color: 0x1a1a1a, alpha: 0.7 });
            container.addChild(graphics);
            break;
          }
          case 'ramp': {
            const graphics = new PIXI.Graphics();
            graphics.rect(x, y, width, height);
            graphics.fill({ color: 0x8B4513, alpha: 0.8 });
            container.addChild(graphics);
            break;
          }
        }
      });
    }

    // Draw track border/outline for visibility
    const border = new PIXI.Graphics();
    border.rect(0, 0, track.width, track.height);
    border.stroke({ color: 0x2a2a4e, width: 4 });
    container.addChildAt(border, 0);
  };

  // Update cars from room state
  useEffect(() => {
    if (!room?.players) return;
    
    const { setCarState } = useGameStore.getState();
    
    room.players.forEach((player) => {
      if (player.position) {
        setCarState(player.id, {
          position: player.position,
          displayPosition: player.position,
          targetPosition: player.position,
          rotation: player.angle || 0,
          displayRotation: player.angle || 0,
          targetRotation: player.angle || 0,
          velocity: player.velocity || { x: 0, y: 0 },
          angularVelocity: 0,
          lap: player.lap,
          checkpoint: player.checkpointIndex,
          finished: player.finished,
        });
      }
    });
  }, [room?.players]);

  return null; // Render happens in PIXI
}

export default GameRenderer;
